import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ActiveDevice } from "../../../api/contentDeviceApi";
import {
  DEFAULT_DEVICE_LIMIT,
  DEVICE_LIMIT_MESSAGE,
} from "../../../constants/device";

/**
 * 기기 등록 초과 안내 단계 (기획 STEP 01 → 03)
 * - select: 초과 안내 + 해제할 기기 선택
 * - confirm: 선택한 기기 해제 확인
 * - waiting: 통합마이페이지를 다녀온 뒤 재시도 대기
 */
export type DeviceLimitStep = "select" | "confirm" | "waiting";

export interface DeviceStoreState {
  /** 기기 등록 판정 요청이 진행 중인지 (중복 전송 방지 + 입력 비활성화용) */
  isChecking: boolean;
  limitModalOpen: boolean;
  step: DeviceLimitStep;
  activeDevices: ActiveDevice[];
  deviceLimit: number;
  /** 해제 대상으로 선택한 기기 id */
  selectedDeviceId: number | null;
  /** 교체/재시도 요청이 진행 중인지 */
  isProcessing: boolean;
  errorMessage: string | null;
  /** 교체 성공 후 이어서 전송할 동작 (입력했던 메시지 재전송) */
  resumeSubmit: (() => void | Promise<void>) | null;
  /** 교체 성공 시 판정 캐시를 갱신하기 위한 정보 */
  userId: string | null;
  conversationKey: string;

  openLimitModal: (payload: {
    activeDevices: ActiveDevice[];
    limit: number | null;
    onResume: () => void | Promise<void>;
    userId: string | null;
    conversationKey: string;
  }) => void;
  closeLimitModal: () => void;
  selectDevice: (deviceId: number) => void;
  moveToConfirmStep: () => void;
  backToSelectStep: () => void;
  moveToWaitingStep: () => void;
  setIsChecking: (isChecking: boolean) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  setErrorMessage: (message: string | null) => void;
}

/**
 * 기기 등록 제한 스토어
 * - 채팅 전송 시 슬롯 초과(409) 안내 모달 상태를 관리
 * - 튜터 / 소크라테스에서 공용으로 사용
 */
export const useDeviceStore = create<DeviceStoreState>()(
  devtools(
    (set) => ({
      isChecking: false,
      limitModalOpen: false,
      step: "select",
      activeDevices: [],
      deviceLimit: DEFAULT_DEVICE_LIMIT,
      selectedDeviceId: null,
      isProcessing: false,
      errorMessage: null,
      resumeSubmit: null,
      userId: null,
      conversationKey: "",

      // 슬롯 초과 안내 열기
      // 이미 열려 있는 상태에서 다시 초과가 오면 "재시도했지만 여전히 초과"인 경우이므로,
      // 안내 단계를 유지한 채 최신 기기 목록과 실패 문구만 갱신한다.
      openLimitModal: ({
        activeDevices,
        limit,
        onResume,
        userId,
        conversationKey,
      }) =>
        set((state) => {
          const isRetryFailure = state.limitModalOpen;

          return {
            limitModalOpen: true,
            step: isRetryFailure ? state.step : "select",
            activeDevices,
            deviceLimit: limit ?? DEFAULT_DEVICE_LIMIT,
            // 목록이 갱신되므로 이전 선택은 버린다
            selectedDeviceId: null,
            isProcessing: false,
            errorMessage: isRetryFailure
              ? DEVICE_LIMIT_MESSAGE.RETRY_FAILED
              : null,
            resumeSubmit: onResume,
            userId,
            conversationKey,
          };
        }),

      // 안내 닫기 (등록 기기 변화 없음, 입력 메시지는 그대로 유지됨)
      closeLimitModal: () =>
        set({
          limitModalOpen: false,
          selectedDeviceId: null,
          isProcessing: false,
          errorMessage: null,
          resumeSubmit: null,
        }),

      selectDevice: (deviceId: number) =>
        set({ selectedDeviceId: deviceId, errorMessage: null }),

      moveToConfirmStep: () => set({ step: "confirm", errorMessage: null }),

      // 해제 확인에서 취소하면 선택 화면으로 돌아간다 (기획 Case 6)
      backToSelectStep: () => set({ step: "select" }),

      // 기기 관리 페이지로 이동한 뒤 재시도 대기 상태로 전환
      moveToWaitingStep: () => set({ step: "waiting", errorMessage: null }),

      setIsChecking: (isChecking: boolean) => set({ isChecking }),

      setIsProcessing: (isProcessing: boolean) => set({ isProcessing }),

      setErrorMessage: (errorMessage: string | null) => set({ errorMessage }),
    }),
    { name: "DeviceStore" }
  )
);
